// ============================================
// Chat Module - Send Message (ES6)
// 메시지 전송 및 SSE 스트리밍 처리
// ============================================

import { CHAT_ENDPOINTS } from '../shared/api-endpoints.js';
import { authFetch } from '../shared/auth-fetch.js';
import { safeJsonParse, handleApiError } from '../utils/error-handling.js';
import { showToast, escapeHtml } from '../utils.js';
import { CURRENCY } from '../constants.js';
import {
  getTtsEnabled,
  getCurrentVoiceId,
  clearQueue,
  resetTtsState,
  unlockAudio,
  processStreamingText,
  flushPendingText
} from './tts/index.js';
import {
  getCurrentUser,
  getCurrentRoomId,
  getCurrentSessionId,
  getCurrentProfile,
  isProcessing,
  setIsProcessing,
  incrementTurnCount,
  decrementTurnCount,
  updateFixedMetaBar
} from '../app/state.js';
import {
  createStreamingMessage,
  setStreamingTargetText,
  finalizeStreamingMessage,
  stopStreaming
} from './messaging/stream.js';
import { getCurrentTurnCount } from '../app/state.js';
import { appendMessage } from './messaging/renderer.js';
import { openProfileSelector } from '../profile/index.js';
import { hideChoicePopup, isChoiceFunctionEnabled, displayChoices } from './message-actions.js';
import { refreshMemoryPanel, invalidateMemoryCache } from '../memory.js';
import { getSelectedModel } from '../settings/state.js';
import { setUserCarbon } from '../auth/state.js';
import { updateCarbonDisplay } from '../auth/user.js';
import { openCarbonRechargeModal } from '../mypage/carbon.js';
import { useChatStatusStore } from '../../stores/chatStatusStore.js';
import { refreshChatListBadges } from './list.js';
import { refreshSessionModalBadges } from './session-modal.js';
import { isChatOverlayActive } from './overlay.js';

// ============================================
// 로딩 표시
// ============================================

export function showLoading() {
  const container = document.getElementById('messagesContainer');
  const div = document.createElement('div');
  div.className = 'message assistant';
  div.id = 'loadingMessage';
  div.innerHTML = `
    <div class="message-content">
      <div class="loading"><span></span><span></span><span></span></div>
    </div>
  `;
  container.appendChild(div);
  container.scrollTop = container.scrollHeight;
}

export function removeLoading() {
  document.getElementById('loadingMessage')?.remove();
}

// ============================================
// 유저 메시지 포맷
// ============================================

export function formatUserMessage(content) {
  let escaped = escapeHtml(content);
  escaped = escaped.replace(/\*\*([^*]+)\*\*/g, '<strong class="bold-text">$1</strong>');
  escaped = escaped.replace(/\*([^*]+)\*/g, '<span class="action-text">$1</span>');
  return escaped;
}

// ============================================
// 메시지 전송
// ============================================

export async function sendMessage(choiceText = null, options = {}) {
  const input = document.getElementById('messageInput');
  const content = choiceText || input.value.trim();

  if (!content || isProcessing()) return;

  // TTS 활성화된 경우 사용자 인터랙션 시점에 오디오 unlock
  if (getTtsEnabled()) {
    unlockAudio();
  }

  if (!getCurrentProfile()) {
    showToast('먼저 프로필을 선택해주세요');
    openProfileSelector();
    return;
  }

  const roomId = getCurrentRoomId();
  const sessionId = getCurrentSessionId();

  if (!roomId || !sessionId) {
    showToast('홈에서 콘텐츠를 선택하거나 내 작품에서 채팅을 시작해주세요', true);
    return;
  }

  // 이미 대기 중인 메시지가 있으면 차단
  const chatStatus = useChatStatusStore.getState();
  const statusKey = sessionId;
  if (chatStatus.isPending(statusKey)) {
    showToast('이전 메시지에 대한 응답을 기다리는 중입니다');
    return;
  }

  setIsProcessing(true);

  // 대기 상태 저장
  chatStatus.setPending(statusKey, {
    userInput: content,
    startedAt: Date.now(),
    status: 'streaming'
  });
  refreshChatListBadges();
  refreshSessionModalBadges(); // 리스트에 pending 배지 표시
  document.getElementById('sendBtn').disabled = true;
  const turnCount = incrementTurnCount();
  hideChoicePopup();

  const isContinueMode = content === '[계속]';
  if (!isContinueMode) {
    appendMessage('user', content, null, null, turnCount);
  }
  input.value = '';
  showLoading();

  let streamingDiv = null; // 스트리밍 메시지 참조 (catch에서 접근 가능)

  // 채팅방 전환 여부 체크 (다른 채팅방이면 UI 렌더링 스킵)
  const isStillSameChat = () => {
    return getCurrentSessionId() === sessionId;
  };

  try {
    // 세션 구조 전용
    const requestBody = {
      userInput: content,
      roomId,
      sessionId,
      settings: {
        model: getSelectedModel()
      }
    };

    // 궁합 요청인 경우 상대방 정보 추가
    if (options.compatibility) {
      requestBody.compatibility = options.compatibility;
    }

    // 타임아웃 설정 (2분)
    const STREAM_TIMEOUT = 120000;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, STREAM_TIMEOUT);

    const response = await authFetch(CHAT_ENDPOINTS.infiniteStreamChat, {
      body: requestBody,
      signal: controller.signal
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 402) {
        showToast(`${CURRENCY.NAME}가 부족합니다`, 'error');
        openCarbonRechargeModal();
        // 402는 throw 없이 early return (catch 블록 토스트 방지)
        if (isStillSameChat()) {
          removeLoading();
          if (!isContinueMode) {
            const messages = document.querySelectorAll('.message.user');
            if (messages.length > 0) messages[messages.length - 1].remove();
            input.value = content;
          }
          decrementTurnCount();
        }
        setIsProcessing(false);
        document.getElementById('sendBtn').disabled = false;
        return;
      }
      throw new Error(errorData.error || 'SSE 연결 실패');
    }

    // 같은 채팅방일 때만 UI 업데이트
    if (isStillSameChat()) {
      updateFixedMetaBar();
      removeLoading();
      streamingDiv = createStreamingMessage();
    }
    let isFinalized = false; // 스트리밍 완료 여부 추적

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullText = '';
    let metadata = null;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          const jsonStr = line.slice(6).trim();
          if (!jsonStr) continue;

          const data = safeJsonParse(jsonStr, null);
          if (!data) continue; // JSON 파싱 실패시 해당 청크 무시

          if (data.type === 'chunk') {
            fullText += data.text;
            // 같은 채팅방이고 스트리밍 메시지가 DOM에 있을 때만 업데이트
            if (isStillSameChat() && streamingDiv && streamingDiv.parentNode) {
              setStreamingTargetText(fullText);
            }
            // TTS 활성화 시 실시간 처리 (대사 블록마다 요청)
            if (getTtsEnabled() && getCurrentVoiceId()) {
              processStreamingText(fullText);
            }

          } else if (data.type === 'done') {
            metadata = data;

            // TTS 마지막 블록 처리 (스트리밍 완료)
            if (getTtsEnabled() && getCurrentVoiceId()) {
              flushPendingText();
            }

            // 같은 채팅방일 때 메시지 완료 처리
            // streamingDiv.parentNode으로 DOM에 있는지 확인 (재진입 시 clearMessages로 제거됨)
            if (isStillSameChat()) {
              if (streamingDiv && streamingDiv.parentNode) {
                // 정상 케이스: 스트리밍 메시지가 DOM에 있음
                finalizeStreamingMessage(streamingDiv, fullText, getCurrentTurnCount());
                if (data.messageId) {
                  streamingDiv.dataset.messageId = data.messageId;
                }
                // 토큰 사용량 정보 저장
                if (data.tokenUsage) {
                  streamingDiv.dataset.tokenUsage = JSON.stringify(data.tokenUsage);
                }
              } else if (isChatOverlayActive()) {
                // 재진입 케이스: 스트리밍 중 나갔다가 다시 들어옴
                // 로딩 애니메이션 제거하고 메시지 직접 추가
                removeLoading();
                // appendMessage(role, content, timestamp, messageId, turn)
                appendMessage('assistant', fullText, null, data.messageId, getCurrentTurnCount());
              }
              // else: 오버레이가 닫혀있으면 Firestore에서 다음에 로드됨
            }
            isFinalized = true;
            clearTimeout(timeoutId);  // 타임아웃 타이머 정리

            // 마지막 메시지 저장 및 대기 상태 해제 (항상 실행)
            useChatStatusStore.getState().setLastMessage(statusKey, fullText);
            useChatStatusStore.getState().clearPending(statusKey);
            refreshChatListBadges();
            refreshSessionModalBadges();

            // 카본 업데이트 (항상 실행)
            if (data.carbon !== undefined) {
              setUserCarbon(data.carbon);
              updateCarbonDisplay();
            }

            // 상태 업데이트는 같은 채팅방일 때만
            if (isStillSameChat() && data.state && window.currentChatData) {
              if (!window.currentChatData.narrativeState) {
                window.currentChatData.narrativeState = { location: {}, time: {}, currentScene: {} };
              }
              if (data.state.location) window.currentChatData.narrativeState.location.current = data.state.location;
              if (data.state.time) window.currentChatData.narrativeState.time.description = data.state.time;
              if (data.state.situation) window.currentChatData.narrativeState.currentScene.description = data.state.situation;
              if (data.state.relationship && window.currentChatData.character) {
                window.currentChatData.character.relationship = data.state.relationship;
              }
              updateFixedMetaBar();
            }

          } else if (data.type === 'safety_blocked') {
            console.warn('Safety blocked:', data.reason);

            // 같은 채팅방일 때만 UI 정리
            if (isStillSameChat()) {
              stopStreaming();
              const streamingMsg = document.querySelector('.message.streaming');
              if (streamingMsg) streamingMsg.remove();
              showToast(data.message || '콘텐츠 정책에 의해 차단되었습니다.', true);
              const userMessages = document.querySelectorAll('.message.user');
              if (userMessages.length > 0) userMessages[userMessages.length - 1].remove();
              decrementTurnCount();
            }

            // 대기 상태 해제 (항상 실행)
            useChatStatusStore.getState().clearPending(statusKey);
            refreshChatListBadges();
            refreshSessionModalBadges();

            setIsProcessing(false);
            document.getElementById('sendBtn').disabled = false;
            return;

          } else if (data.type === 'error') {
            // 카본 부족 에러 처리
            if (data.message?.includes('부족')) {
              showToast(`${CURRENCY.NAME}가 부족합니다`, 'error');
              openCarbonRechargeModal();
              if (isStillSameChat()) {
                removeLoading();
                if (streamingDiv?.parentNode) streamingDiv.remove();
                decrementTurnCount();
              }
              setIsProcessing(false);
              document.getElementById('sendBtn').disabled = false;
              return;
            }
            throw new Error(data.message || '스트리밍 오류');
          }
        }
      }
    }

    // 같은 채팅방일 때만 선택지 표시 및 메모리 패널 렌더링
    if (isStillSameChat()) {
      if (metadata?.choices?.length > 0 && isChoiceFunctionEnabled()) {
        displayChoices(metadata.choices);
      }
      // 캐시 무효화 (state, memories만 - 다음 조회 시 새로고침)
      invalidateMemoryCache();
      // 메모리 패널이 열려있을 때만 새로고침
      const memoryPanel = document.getElementById('memoryPanel');
      if (memoryPanel?.classList.contains('active')) {
        refreshMemoryPanel();
      }
    }

  } catch (e) {
    // 타임아웃 타이머 정리
    clearTimeout(timeoutId);

    // TTS 큐 정리
    clearQueue();
    resetTtsState();

    // 같은 채팅방일 때만 UI 정리
    if (isStillSameChat()) {
      removeLoading();
      stopStreaming();

      // 스트리밍 메시지 제거 (finalized 여부와 관계없이)
      if (streamingDiv && streamingDiv.parentNode) {
        streamingDiv.remove();
      } else {
        // 혹시 참조가 없으면 쿼리로 시도
        const streamingMsg = document.querySelector('.message.streaming');
        if (streamingMsg) streamingMsg.remove();
      }

      if (!isContinueMode) {
        const messages = document.querySelectorAll('.message.user');
        if (messages.length > 0) messages[messages.length - 1].remove();
        input.value = content;
      }
      decrementTurnCount();

      // 에러 유형별 처리
      if (e.name === 'AbortError') {
        showToast('응답 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.', 'error');
      } else if (e.message?.includes('부족')) {
        showToast(`${CURRENCY.NAME}가 부족합니다`, 'error');
        openCarbonRechargeModal();
      } else {
        showToast('API 오류가 발생했습니다. 다시 시도해주세요.', true);
      }
    }

    // 대기 상태 해제 (항상 실행)
    useChatStatusStore.getState().clearPending(statusKey);
    refreshChatListBadges();
            refreshSessionModalBadges();

    console.error('[sendMessage] 에러:', e.message, e);
  }

  setIsProcessing(false);
  document.getElementById('sendBtn').disabled = false;
}

// ============================================
// window 노출 제거됨 (2026-01-06)
// 모든 호출이 ES6 import로 처리됨
// ============================================
